// src/pages/reports/SalesReportPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp,
  ShoppingCart,
  Package,
  DollarSign,
  Filter,
  Download,
  RefreshCw,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import MultiSelectFilter from '../../components/common/MultiSelectFilter';
import ReportDatePresetFilter, { getPresets } from '../../components/common/ReportDatePresetFilter/ReportDatePresetFilter';
import { getSalesReportFilters, getSalesSummary } from '../../services/api/salesReport';
import { getSalesReportSettings, saveSalesReportSettings } from '../../services/api/salesReportSettings';
import { getSalesReps } from '../../services/api/offlineSales/orders';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { useAuth } from '../../hooks/useAuth';
import { formatCurrency } from '../../utils/currencyUtil';
import styles from './SalesReportPage.module.css';

const SALES_DATE_PRESETS = ['today', 'thisWeek', 'thisMonth', 'lastMonth', 'thisYear'];
const SALES_CHANNELS = ['online', 'offline'];
const getThisMonthRange = () => getPresets().find(p => p.key === 'thisMonth');

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const SalesReportPage = () => {
  const { user } = useAuth();
  const { isAdmin } = usePermissions();
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();

  // Filter Options State
  const [filterOptions, setFilterOptions] = useState({
    shops: [],
    dispatchedStatuses: [],
    paymentTypes: [],
    dateRangePresets: {}
  });
  const [excludeDeliveryCharge, setExcludeDeliveryCharge] = useState(false);
  const [isSavingDeliverySetting, setIsSavingDeliverySetting] = useState(false);
  const [isLoadingFilters, setIsLoadingFilters] = useState(true);
  const [salesReps, setSalesReps] = useState([]);

  // Filter State
  const [filters, setFilters] = useState({
    groupByDate: 'date',
    filterByDate: 'dispatchedDate',
    groupByProduct: false,
    shopIds: [],
    statusIds: [],
    paymentTypeIds: [],
    salesRepUserIds: [],
    groupBySalesRep: false,
    channels: SALES_CHANNELS,
    dateFrom: getThisMonthRange().from,
    dateTo: getThisMonthRange().to
  });

  const [showFilters, setShowFilters] = useState(true);

  // Data State
  const [summaryData, setSummaryData] = useState(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);

  // Chart and Table Display State
  const [activeMetric, setActiveMetric] = useState('revenue'); // revenue, quantity, orderCount
  const [visibleColumns, setVisibleColumns] = useState({
    revenue: true,
    quantity: true,
    orderCount: true
  });

  // Pagination State for Pivoted Table
  const [productPage, setProductPage] = useState(1);
  const [productPageSize, setProductPageSize] = useState(10);
  const [periodPage, setPeriodPage] = useState(1);
  const [periodPageSize, setPeriodPageSize] = useState(10);

  // Load filter options on mount
  useEffect(() => {
    loadFilterOptions();
  }, []);

  // Auto-fetch data when filters are set
  useEffect(() => {
    if (filters.dateFrom && filters.dateTo && filterOptions.shops.length > 0) {
      handleApplyFilters();
    }
  }, [filters.dateFrom, filters.dateTo, filters.groupByProduct, filters.groupByDate, filterOptions.shops, excludeDeliveryCharge]);

  const loadFilterOptions = async () => {
    try {
      setIsLoadingFilters(true);
      const data = await getSalesReportFilters();
      setFilterOptions(data);
      setExcludeDeliveryCharge(!!data.excludeDeliveryChargeFromSales);
      setFilters(prev => ({
        ...prev,
        shopIds: (data.shops || []).map(shop => shop.id),
        paymentTypeIds: (data.paymentTypes || []).map(paymentType => paymentType.id)
      }));
      // Sales reps are optional — users without offline-sales access simply don't get the filter
      getSalesReps().then(res => setSalesReps(res?.salesReps || [])).catch(() => setSalesReps([]));
    } catch (error) {
      console.error('Failed to load filters:', error);
      showError(t('report.failedToLoadFilters'));
    } finally {
      setIsLoadingFilters(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [field]: value };
      
      // If switching to Product View, set groupByDate to false by default
      if (field === 'groupByProduct' && value === true && !prev.groupByDate) {
        newFilters.groupByDate = false;
      }
      
      return newFilters;
    });
  };

  const handleStatusToggle = (statusId) => {
    setFilters(prev => {
      const currentIds = prev.statusIds || [];
      const newIds = currentIds.includes(statusId)
        ? currentIds.filter(id => id !== statusId)
        : [...currentIds, statusId];
      return { ...prev, statusIds: newIds };
    });
  };

  const handleChannelToggle = (channel) => {
    setFilters(prev => {
      const current = prev.channels || [];
      const next = current.includes(channel)
        ? current.filter(c => c !== channel)
        : [...current, channel];
      return { ...prev, channels: next };
    });
  };

  const handleExcludeDeliveryChargeChange = async (e) => {
    const value = e.target.checked;
    try {
      setIsSavingDeliverySetting(true);
      const current = await getSalesReportSettings();
      await saveSalesReportSettings({ ...current, excludeDeliveryCharge: value });
      setExcludeDeliveryCharge(value);
      success(t('report.deliveryChargeSettingSaved'));
    } catch (error) {
      showError(error.response?.data?.message || t('report.deliveryChargeSettingFailed'));
    } finally {
      setIsSavingDeliverySetting(false);
    }
  };

  const validateFilters = () => {
    if (!filters.dateFrom) {
      showError(t('report.dateFromRequired'));
      return false;
    }
    if (!filters.dateTo) {
      showError(t('report.dateToRequired'));
      return false;
    }
    if (new Date(filters.dateTo) < new Date(filters.dateFrom)) {
      showError(t('report.dateRangeInvalid'));
      return false;
    }
    return true;
  };

  const handleApplyFilters = async () => {
    if (!validateFilters()) return;
    if (filters.shopIds.length === 0) {
      showError(t('report.selectAtLeastOneShop'));
      return;
    }
    if (filters.paymentTypeIds.length === 0) {
      showError(t('report.selectAtLeastOnePaymentType'));
      return;
    }
    if (filters.channels.length === 0) {
      showError(t('report.selectAtLeastOneChannel'));
      return;
    }

    try {
      setIsLoadingSummary(true);

      const params = {
        groupByDate: filters.groupByDate,
        filterByDate: filters.filterByDate,
        groupByProduct: filters.groupByProduct,
        shopIds: filters.shopIds.length === filterOptions.shops.length
          ? undefined
          : filters.shopIds.join(','),
        statusIds: filters.statusIds.length > 0 ? filters.statusIds.join(',') : undefined,
        paymentTypeIds: filters.paymentTypeIds.length === filterOptions.paymentTypes.length
          ? undefined
          : filters.paymentTypeIds.join(','),
        channels: filters.channels.length === SALES_CHANNELS.length ? undefined : filters.channels.join(','),
        salesRepUserIds: filters.salesRepUserIds.length > 0 ? filters.salesRepUserIds.join(',') : undefined,
        groupBySalesRep: filters.groupBySalesRep || undefined,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo
      };

      const summary = await getSalesSummary(params);
      setSummaryData(summary);
      
      // Reset pagination
      setProductPage(1);
      setPeriodPage(1);
    } catch (error) {
      console.error('Failed to load data:', error);
      showError(error.response?.data?.message || t('report.failedToLoadData'));
    } finally {
      setIsLoadingSummary(false);
    }
  };

  const handleResetFilters = () => {
    const thisMonth = getThisMonthRange();
    setFilters({
      groupByDate: false,
      filterByDate: 'dispatchedDate',
      groupByProduct: false,
      shopIds: filterOptions.shops.map(shop => shop.id),
      statusIds: [],
      paymentTypeIds: filterOptions.paymentTypes.map(paymentType => paymentType.id),
      salesRepUserIds: [],
      groupBySalesRep: false,
      channels: SALES_CHANNELS,
      dateFrom: thisMonth?.from || '',
      dateTo: thisMonth?.to || ''
    });
    setSummaryData(null);
    setProductPage(1);
    setPeriodPage(1);
  };

  const handleExportExcel = async () => {
    try {
      const { default: ExcelJS } = await import('exceljs');
      
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Sales Report');

      // Add title
      worksheet.mergeCells('A1:F1');
      const titleCell = worksheet.getCell('A1');
      titleCell.value = 'Sales Report';
      titleCell.font = { size: 16, bold: true };
      titleCell.alignment = { horizontal: 'center' };

      // Add filter info
      worksheet.addRow([]);
      worksheet.addRow(['Date Range:', `${filters.dateFrom} to ${filters.dateTo}`]);
      worksheet.addRow(['Grouping:', filters.groupByDate || 'None']);
      worksheet.addRow(['View:', filters.groupByProduct ? 'Product View' : 'Time Period View']);
      worksheet.addRow([]);

      if (filters.groupByProduct && filters.groupByDate) {
        // Pivoted table export
        const pivotedData = getPivotedTableData();
        
        // Headers: Product Name + all periods
        const headers = ['Product Name'];
        pivotedData.periods.forEach(period => {
          if (visibleColumns.revenue) headers.push(`${period} - Revenue`);
          if (visibleColumns.quantity) headers.push(`${period} - Quantity`);
          if (visibleColumns.orderCount) headers.push(`${period} - Orders`);
        });
        
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE5E7EB' }
        };

        // Data rows
        pivotedData.products.forEach(product => {
          const rowData = [product.productName];
          pivotedData.periods.forEach(period => {
            const cellData = pivotedData.data[product.rowKey]?.[period] || {
              totalRevenue: 0,
              totalQuantitySold: 0,
              orderCount: 0
            };
            if (visibleColumns.revenue) rowData.push(cellData.totalRevenue);
            if (visibleColumns.quantity) rowData.push(cellData.totalQuantitySold);
            if (visibleColumns.orderCount) rowData.push(cellData.orderCount);
          });
          worksheet.addRow(rowData);
        });
      } else if (filters.groupByProduct) {
        // Product view export
        const headers = ['Product Name', 'Type', 'Order Count', 'Quantity Sold', 'Total Revenue'];
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE5E7EB' }
        };

        summaryData.data.forEach(item => {
          worksheet.addRow([
            item.productName,
            item.productType,
            item.orderCount,
            item.totalQuantitySold,
            item.totalRevenue
          ]);
        });
      } else {
        // Time period view export
        const headers = ['Period', 'Order Count', 'Total Quantity', 'Total Value'];
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE5E7EB' }
        };

        summaryData.data.forEach(item => {
          worksheet.addRow([
            item.label,
            item.orderCount,
            item.totalQuantity,
            item.totalValue
          ]);
        });
      }

      // Add totals
      if (summaryData?.totals) {
        worksheet.addRow([]);
        const totalsRow = worksheet.addRow([
          'TOTALS',
          summaryData.totals.totalOrders,
          summaryData.totals.totalQuantity,
          summaryData.totals.totalValue
        ]);
        totalsRow.font = { bold: true };
      }

      // Auto-fit columns
      worksheet.columns.forEach(column => {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, cell => {
          const cellLength = cell.value ? cell.value.toString().length : 10;
          if (cellLength > maxLength) {
            maxLength = cellLength;
          }
        });
        column.width = Math.min(maxLength + 2, 50);
      });

      // Generate filename
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `sales_report_${timestamp}.xlsx`;

      // Download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);

      success(t('report.exportSuccess'));
    } catch (error) {
      console.error('Export failed:', error);
      showError(t('report.exportFailed'));
    }
  };

  const formatNumber = (value) => {
    return parseFloat(value).toFixed(2);
  };

  // Get pivoted table data (products as rows, periods as columns)
  const getPivotedTableData = () => {
    if (!summaryData?.data || summaryData.data.length === 0) {
      return { products: [], periods: [], data: {} };
    }

    // Get unique products and periods
    const rowKey = (item) => `${item.productId}-${item.variationId ?? 0}`;
    const productsMap = new Map();
    const periodsMap = new Map();

    summaryData.data.forEach(item => {
      if (!productsMap.has(rowKey(item))) {
        productsMap.set(rowKey(item), {
          rowKey: rowKey(item),
          productId: item.productId,
          productName: item.productName,
          productType: item.productType
        });
      }
      periodsMap.set(item.label, item.period);
    });

    const products = Array.from(productsMap.values());
    const periods = Array.from(periodsMap.keys()).sort((a, b) =>
      (new Date(periodsMap.get(a)) - new Date(periodsMap.get(b))) || a.localeCompare(b)
    );

    // Build data matrix
    const data = {};
    summaryData.data.forEach(item => {
      if (!data[rowKey(item)]) {
        data[rowKey(item)] = {};
      }
      data[rowKey(item)][item.label] = {
        totalRevenue: item.totalRevenue,
        totalQuantitySold: item.totalQuantitySold,
        orderCount: item.orderCount
      };
    });

    return { products, periods, data };
  };

  // Render chart visualization
  const renderChart = () => {
    if (!summaryData || !summaryData.data || summaryData.data.length === 0) {
      return (
        <div className={styles.emptyChart}>
          <BarChart3 size={48} />
          <p>{t('report.noDataAvailable')}</p>
        </div>
      );
    }

    // For product + time period view, don't show chart
    if (filters.groupByProduct && filters.groupByDate) {
      return null;
    }

    let chartData, chartOptions;

    if (filters.groupByProduct) {
      // Product view chart
      const sortedData = [...summaryData.data].sort((a, b) => 
        b.totalRevenue - a.totalRevenue
      ).slice(0, 20); // Show top 20 products

      if (activeMetric === 'revenue') {
        chartData = {
          labels: sortedData.map(item => item.productName),
          datasets: [{
            label: t('report.revenue'),
            data: sortedData.map(item => item.totalRevenue),
            backgroundColor: 'rgba(102, 126, 234, 0.8)',
            borderColor: 'rgba(102, 126, 234, 1)',
            borderWidth: 1
          }]
        };
      } else if (activeMetric === 'quantity') {
        chartData = {
          labels: sortedData.map(item => item.productName),
          datasets: [{
            label: t('report.quantity'),
            data: sortedData.map(item => item.totalQuantitySold),
            backgroundColor: 'rgba(16, 185, 129, 0.8)',
            borderColor: 'rgba(16, 185, 129, 1)',
            borderWidth: 1
          }]
        };
      } else {
        chartData = {
          labels: sortedData.map(item => item.productName),
          datasets: [{
            label: t('report.orderCount'),
            data: sortedData.map(item => item.orderCount),
            backgroundColor: 'rgba(245, 158, 11, 0.8)',
            borderColor: 'rgba(245, 158, 11, 1)',
            borderWidth: 1
          }]
        };
      }

      chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'top' },
          tooltip: {
            callbacks: {
              label: function(context) {
                let label = context.dataset.label || '';
                if (label) label += ': ';
                if (activeMetric === 'revenue') {
                  label += formatCurrency(context.parsed.y);
                } else {
                  label += formatNumber(context.parsed.y);
                }
                return label;
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function(value) {
                return activeMetric === 'revenue' 
                  ? formatCurrency(value) 
                  : value.toLocaleString();
              }
            }
          },
          x: {
            ticks: {
              maxRotation: 45,
              minRotation: 45
            }
          }
        }
      };

      return <Bar data={chartData} options={chartOptions} />;
    } else {
      // Time period view chart
      if (activeMetric === 'revenue') {
        chartData = {
          labels: summaryData.data.map(item => item.label),
          datasets: [{
            label: t('report.revenue'),
            data: summaryData.data.map(item => item.totalValue),
            borderColor: 'rgba(102, 126, 234, 1)',
            backgroundColor: 'rgba(102, 126, 234, 0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.4
          }]
        };
      } else if (activeMetric === 'quantity') {
        chartData = {
          labels: summaryData.data.map(item => item.label),
          datasets: [{
            label: t('report.quantity'),
            data: summaryData.data.map(item => item.totalQuantity),
            borderColor: 'rgba(16, 185, 129, 1)',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.4
          }]
        };
      } else {
        chartData = {
          labels: summaryData.data.map(item => item.label),
          datasets: [{
            label: t('report.orderCount'),
            data: summaryData.data.map(item => item.orderCount),
            borderColor: 'rgba(245, 158, 11, 1)',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.4
          }]
        };
      }

      chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'top' },
          tooltip: {
            callbacks: {
              label: function(context) {
                let label = context.dataset.label || '';
                if (label) label += ': ';
                if (activeMetric === 'revenue') {
                  label += formatCurrency(context.parsed.y);
                } else {
                  label += formatNumber(context.parsed.y);
                }
                return label;
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function(value) {
                return activeMetric === 'revenue' 
                  ? formatCurrency(value) 
                  : value.toLocaleString();
              }
            }
          }
        }
      };

      return <Line data={chartData} options={chartOptions} />;
    }
  };

  // Render standard table (time period or product only)
  const renderStandardTable = () => {
    if (!summaryData?.data || summaryData.data.length === 0) {
      return (
        <div className={styles.emptyTable}>
          <Package size={48} />
          <h3>{t('report.noOrdersFound')}</h3>
          <p>{t('report.noOrdersMessage')}</p>
        </div>
      );
    }

    return (
      <table className={styles.dataTable}>
        <thead>
          <tr>
            <th>{filters.groupByProduct ? t('report.productName') : t('report.period')}</th>
            {filters.groupByProduct && <th>{t('report.productType')}</th>}
            <th>{t('report.orderCount')}</th>
            <th>{t('report.quantitySold')}</th>
            <th>{t('report.totalValue')}</th>
          </tr>
        </thead>
        <tbody>
          {summaryData.data.map((row, index) => (
            <tr key={index} className={styles.dataRow}>
              <td className={styles.primaryCell}>
                {filters.groupByProduct ? row.productName : row.label}
              </td>
              {filters.groupByProduct && (
                <td>
                  <span className={styles.typeBadge}>
                    {row.productType === 'variable' ? t('report.variable') : t('report.simple')}
                  </span>
                </td>
              )}
              <td>{row.orderCount}</td>
              <td>{formatNumber(filters.groupByProduct ? row.totalQuantitySold : row.totalQuantity)}</td>
              <td className={styles.valueCell}>
                {formatCurrency(filters.groupByProduct ? row.totalRevenue : row.totalValue)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  // Render pivoted table (products as rows, periods as columns)
  const renderPivotedTable = () => {
    const pivotedData = getPivotedTableData();
    
    if (pivotedData.products.length === 0 || pivotedData.periods.length === 0) {
      return (
        <div className={styles.emptyTable}>
          <Package size={48} />
          <h3>{t('report.noOrdersFound')}</h3>
          <p>{t('report.noOrdersMessage')}</p>
        </div>
      );
    }

    // Paginate products
    const startProductIdx = (productPage - 1) * productPageSize;
    const endProductIdx = startProductIdx + productPageSize;
    const paginatedProducts = pivotedData.products.slice(startProductIdx, endProductIdx);

    // Paginate periods
    const startPeriodIdx = (periodPage - 1) * periodPageSize;
    const endPeriodIdx = startPeriodIdx + periodPageSize;
    const paginatedPeriods = pivotedData.periods.slice(startPeriodIdx, endPeriodIdx);

    const totalProductPages = Math.ceil(pivotedData.products.length / productPageSize);
    const totalPeriodPages = Math.ceil(pivotedData.periods.length / periodPageSize);

    return (
      <>
        {/* Column visibility controls */}
        <div className={styles.columnControls}>
          <label className={styles.columnToggle}>
            <input
              type="checkbox"
              checked={visibleColumns.revenue}
              onChange={(e) => setVisibleColumns(prev => ({ ...prev, revenue: e.target.checked }))}
            />
            <span>{t('report.revenue')}</span>
          </label>
          <label className={styles.columnToggle}>
            <input
              type="checkbox"
              checked={visibleColumns.quantity}
              onChange={(e) => setVisibleColumns(prev => ({ ...prev, quantity: e.target.checked }))}
            />
            <span>{t('report.quantity')}</span>
          </label>
          <label className={styles.columnToggle}>
            <input
              type="checkbox"
              checked={visibleColumns.orderCount}
              onChange={(e) => setVisibleColumns(prev => ({ ...prev, orderCount: e.target.checked }))}
            />
            <span>{t('report.orderCount')}</span>
          </label>
        </div>

        {/* Period pagination */}
        {totalPeriodPages > 1 && (
          <div className={styles.periodPagination}>
            <button
              onClick={() => setPeriodPage(p => Math.max(1, p - 1))}
              disabled={periodPage === 1}
              className={styles.pageButton}
            >
              <ChevronLeft size={16} />
            </button>
            <span className={styles.pageInfo}>
              Periods {startPeriodIdx + 1}-{Math.min(endPeriodIdx, pivotedData.periods.length)} of {pivotedData.periods.length}
            </span>
            <button
              onClick={() => setPeriodPage(p => Math.min(totalPeriodPages, p + 1))}
              disabled={periodPage === totalPeriodPages}
              className={styles.pageButton}
            >
              <ChevronRight size={16} />
            </button>
            <select
              value={periodPageSize}
              onChange={(e) => {
                setPeriodPageSize(Number(e.target.value));
                setPeriodPage(1);
              }}
              className={styles.pageSizeSelect}
            >
              <option value={5}>5 periods</option>
              <option value={10}>10 periods</option>
              <option value={20}>20 periods</option>
            </select>
          </div>
        )}

        <div className={styles.pivotedTableWrapper}>
          <table className={styles.pivotedTable}>
            <thead>
              <tr>
                <th className={styles.stickyColumn}>{t('report.productName')}</th>
                {paginatedPeriods.map(period => (
                  <th key={period} colSpan={
                    (visibleColumns.revenue ? 1 : 0) +
                    (visibleColumns.quantity ? 1 : 0) +
                    (visibleColumns.orderCount ? 1 : 0)
                  }>
                    {period}
                  </th>
                ))}
              </tr>
              <tr>
                <th className={styles.stickyColumn}></th>
                {paginatedPeriods.map(period => (
                  <React.Fragment key={period}>
                    {visibleColumns.revenue && <th className={styles.subHeader}>Rev</th>}
                    {visibleColumns.quantity && <th className={styles.subHeader}>Qty</th>}
                    {visibleColumns.orderCount && <th className={styles.subHeader}>Ord</th>}
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedProducts.map(product => (
                <tr key={product.rowKey}>
                  <td className={`${styles.primaryCell} ${styles.stickyColumn}`}>
                    {product.productName}
                  </td>
                  {paginatedPeriods.map(period => {
                    const cellData = pivotedData.data[product.rowKey]?.[period] || {
                      totalRevenue: 0,
                      totalQuantitySold: 0,
                      orderCount: 0
                    };
                    return (
                      <React.Fragment key={period}>
                        {visibleColumns.revenue && (
                          <td className={styles.numericCell}>
                            {formatCurrency(cellData.totalRevenue)}
                          </td>
                        )}
                        {visibleColumns.quantity && (
                          <td className={styles.numericCell}>
                            {formatNumber(cellData.totalQuantitySold)}
                          </td>
                        )}
                        {visibleColumns.orderCount && (
                          <td className={styles.numericCell}>
                            {cellData.orderCount}
                          </td>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Product pagination */}
        {totalProductPages > 1 && (
          <div className={styles.productPagination}>
            <button
              onClick={() => setProductPage(p => Math.max(1, p - 1))}
              disabled={productPage === 1}
              className={styles.pageButton}
            >
              <ChevronLeft size={16} />
            </button>
            <span className={styles.pageInfo}>
              Products {startProductIdx + 1}-{Math.min(endProductIdx, pivotedData.products.length)} of {pivotedData.products.length}
            </span>
            <button
              onClick={() => setProductPage(p => Math.min(totalProductPages, p + 1))}
              disabled={productPage === totalProductPages}
              className={styles.pageButton}
            >
              <ChevronRight size={16} />
            </button>
            <select
              value={productPageSize}
              onChange={(e) => {
                setProductPageSize(Number(e.target.value));
                setProductPage(1);
              }}
              className={styles.pageSizeSelect}
            >
              <option value={5}>5 products</option>
              <option value={10}>10 products</option>
              <option value={20}>20 products</option>
              <option value={50}>50 products</option>
            </select>
          </div>
        )}
      </>
    );
  };

  // Determine if chart should be shown
  const shouldShowChart = summaryData && summaryData.data.length > 0 && 
    !(filters.groupByProduct && filters.groupByDate);

  return (
    <div className={styles.salesReportPage}>
      {/* Page Header */}
      <PageHeader
        title={t('report.salesReport')}
        description={t('report.salesReportSubtitle')}
        icon={TrendingUp}
        centerActions
        actions={
          <label className={styles.headerToggle}>
            <span className={styles.headerToggleLabel}>{t('report.excludeDeliveryCharge')}</span>
            <span className={styles.switch}>
              <input
                type="checkbox"
                checked={excludeDeliveryCharge}
                onChange={handleExcludeDeliveryChargeChange}
                disabled={!isAdmin() || isSavingDeliverySetting}
              />
              <span className={styles.slider} />
            </span>
          </label>
        }
      />

      {/* Filter Section */}
      <div className={styles.filterSection}>
        <div className={styles.filterHeader}>
          <div className={styles.filterHeaderLeft}>
            <Filter size={20} />
            <h2>{t('report.filters')}</h2>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={styles.filterToggle}
          >
            {showFilters ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        </div>

        {showFilters && (
          <div className={styles.filterContent}>
            {/* Date Range Presets */}
            <div className={styles.filterGroup}>
              <label>{t('report.dateRange')}</label>
              <ReportDatePresetFilter
                presets={SALES_DATE_PRESETS}
                value={{ dateFrom: filters.dateFrom, dateTo: filters.dateTo }}
                onChange={({ dateFrom, dateTo }) => setFilters(prev => ({ ...prev, dateFrom, dateTo }))}
              />
            </div>

            {/* View Type and Grouping */}
            <div className={styles.filterRow}>
              <div className={styles.filterGroup}>
                <label>{t('report.viewType')}</label>
                <div className={styles.radioGroup}>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      checked={!filters.groupByProduct}
                      onChange={() => handleFilterChange('groupByProduct', false)}
                      className={styles.radio}
                    />
                    <span>{t('report.timePeriodView')}</span>
                  </label>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      checked={filters.groupByProduct}
                      onChange={() => handleFilterChange('groupByProduct', true)}
                      className={styles.radio}
                    />
                    <span>{t('report.productView')}</span>
                  </label>
                </div>
              </div>

              <div className={styles.filterGroup}>
                <label>{t('report.grouping')}</label>
                <div className={styles.radioGroup}>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      value={false}
                      checked={filters.groupByDate === false}
                      onChange={() => handleFilterChange('groupByDate', false)}
                      className={styles.radio}
                      disabled={!filters.groupByProduct}
                    />
                    <span>None</span>
                  </label>
                  {['date', 'week', 'month'].map(group => (
                    <label key={group} className={styles.radioLabel}>
                      <input
                        type="radio"
                        value={group}
                        checked={filters.groupByDate === group}
                        onChange={(e) => handleFilterChange('groupByDate', e.target.value)}
                        className={styles.radio}
                      />
                      <span>{t(`report.groupBy${group.charAt(0).toUpperCase() + group.slice(1)}`)}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Date Type and Shop */}
            <div className={styles.filterRow}>
              <div className={styles.filterGroup}>
                <label>{t('report.filterByDateType')} *</label>
                <div className={styles.radioGroup}>
                  {['createdAt', 'dispatchedDate'].map(dateType => (
                    <label key={dateType} className={styles.radioLabel}>
                      <input
                        type="radio"
                        value={dateType}
                        checked={filters.filterByDate === dateType}
                        onChange={(e) => handleFilterChange('filterByDate', e.target.value)}
                        className={styles.radio}
                      />
                      <span>{t(`report.${dateType === 'createdAt' ? 'createdDate' : 'dispatchedDate'}`)}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className={styles.filterGroup}>
                <MultiSelectFilter
                  label={t('report.shop')}
                  options={filterOptions.shops}
                  selectedValues={filters.shopIds}
                  onChange={(values) => handleFilterChange('shopIds', values)}
                  placeholder={t('report.selectShops')}
                  displayKey="name"
                  valueKey="id"
                  searchKey="name"
                  showSelectAll
                  floatingDropdown
                  selectAllLabel={t('report.selectAll')}
                  deselectAllLabel={t('report.deselectAll')}
                />
              </div>
            </div>

            <div className={styles.filterRow}>
              {/* Status Filter */}
              <div className={styles.filterGroup}>
                <label>{t('report.orderStatus')}</label>
                <div className={styles.statusFilters}>
                  {filterOptions.dispatchedStatuses.map(status => (
                    <label key={status.id} className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={filters.statusIds.includes(status.id)}
                        onChange={() => handleStatusToggle(status.id)}
                        className={styles.checkbox}
                      />
                      <span
                        className={styles.statusBadge}
                        style={{ backgroundColor: status.colorCode || '#9ca3af' }}
                      >
                        {status.label}
                      </span>
                    </label>
                  ))}
                </div>
                {filters.statusIds.length === 0 && (
                  <span className={styles.helperText}>{t('report.allStatuses')}</span>
                )}
                <span className={styles.helperText}>{t('report.statusAppliesToOnline')}</span>
              </div>

              <div className={styles.filterGroup}>
                <label>{t('report.channel')}</label>
                <div className={styles.statusFilters}>
                  {SALES_CHANNELS.map(channel => (
                    <label key={channel} className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={filters.channels.includes(channel)}
                        onChange={() => handleChannelToggle(channel)}
                        className={styles.checkbox}
                      />
                      <span>{t(`report.channel${channel === 'online' ? 'Online' : 'Offline'}`)}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className={styles.filterRow}>
              <div className={styles.filterGroup}>
                <MultiSelectFilter
                  label={t('report.paymentType')}
                  options={filterOptions.paymentTypes}
                  selectedValues={filters.paymentTypeIds}
                  onChange={(values) => handleFilterChange('paymentTypeIds', values)}
                  placeholder={t('report.selectPaymentTypes')}
                  displayKey="label"
                  valueKey="id"
                  searchKey="label"
                  showSelectAll
                  floatingDropdown
                  selectAllLabel={t('report.selectAll')}
                  deselectAllLabel={t('report.deselectAll')}
                />
              </div>
              {salesReps.length > 0 && (
                <div className={styles.filterGroup}>
                  <MultiSelectFilter
                    label={t('report.salesRep')}
                    options={salesReps}
                    selectedValues={filters.salesRepUserIds}
                    onChange={(values) => handleFilterChange('salesRepUserIds', values)}
                    placeholder={t('report.allSalesReps')}
                    displayKey="name"
                    valueKey="id"
                    searchKey="name"
                    showSelectAll
                    floatingDropdown
                    selectAllLabel={t('report.selectAll')}
                    deselectAllLabel={t('report.deselectAll')}
                  />
                  <label className={styles.helperText}>
                    <input
                      type="checkbox"
                      checked={filters.groupBySalesRep}
                      onChange={(e) => handleFilterChange('groupBySalesRep', e.target.checked)}
                    />{' '}
                    {t('report.groupBySalesRep')}
                  </label>
                </div>
              )}
            </div>

            {/* Filter Actions */}
            <div className={styles.filterActions}>
              <button
                onClick={handleResetFilters}
                className={styles.resetButton}
                disabled={isLoadingSummary}
              >
                <RefreshCw size={18} />
                {t('report.resetFilters')}
              </button>
              <button
                onClick={handleApplyFilters}
                className={styles.applyButton}
                disabled={isLoadingSummary}
              >
                {isLoadingSummary ? (
                  <>
                    <Loader2 className={styles.spinner} size={18} />
                    {t('report.applying')}
                  </>
                ) : (
                  <>
                    <BarChart3 size={18} />
                    {t('report.applyFilters')}
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      {summaryData && !isLoadingSummary && (
        <div className={styles.summaryCards}>
          <div className={styles.summaryCard}>
            <div className={styles.cardIcon} style={{ backgroundColor: '#dbeafe' }}>
              <ShoppingCart size={24} style={{ color: '#1e40af' }} />
            </div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>{t('report.totalOrders')}</div>
              <div className={styles.cardValue}>{summaryData.totals.totalOrders}</div>
              <div className={styles.cardSubtext}>{t('report.orders')}</div>
            </div>
          </div>

          <div className={styles.summaryCard}>
            <div className={styles.cardIcon} style={{ backgroundColor: '#dcfce7' }}>
              <DollarSign size={24} style={{ color: '#15803d' }} />
            </div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>{t('report.totalRevenue')}</div>
              <div className={styles.cardValue}>{formatCurrency(summaryData.totals.totalValue)}</div>
              <div className={styles.cardSubtext}>{user?.business?.currency}</div>
            </div>
          </div>

          <div className={styles.summaryCard}>
            <div className={styles.cardIcon} style={{ backgroundColor: '#fef3c7' }}>
              <Package size={24} style={{ color: '#b45309' }} />
            </div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>{t('report.totalQuantity')}</div>
              <div className={styles.cardValue}>{formatNumber(summaryData.totals.totalQuantity)}</div>
              <div className={styles.cardSubtext}>{t('report.items')}</div>
            </div>
          </div>
        </div>
      )}

      {/* Sales Rep Breakdown */}
      {summaryData?.salesRepBreakdown && !isLoadingSummary && (
        <div className={styles.tableSection}>
          <div className={styles.tableHeader}>
            <h2>{t('report.salesRepBreakdown')}</h2>
          </div>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>{t('report.salesRep')}</th>
                <th>{t('report.orderCount')}</th>
                <th>{t('report.totalValue')}</th>
              </tr>
            </thead>
            <tbody>
              {summaryData.salesRepBreakdown.map(row => (
                <tr key={row.salesRepUserId || 'unassigned'} className={styles.dataRow}>
                  <td className={styles.primaryCell}>{row.salesRepName || t('report.unassignedSalesRep')}</td>
                  <td>{row.orderCount}</td>
                  <td className={styles.valueCell}>{formatCurrency(row.totalValue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Chart Visualization */}
      {shouldShowChart && (
        <div className={styles.chartSection}>
          <div className={styles.chartHeader}>
            <div className={styles.chartHeaderLeft}>
              <h2>{t('report.chartVisualization')}</h2>
              <div className={styles.metricToggle}>
                <button
                  className={`${styles.metricButton} ${activeMetric === 'revenue' ? styles.active : ''}`}
                  onClick={() => setActiveMetric('revenue')}
                >
                  {t('report.revenue')}
                </button>
                <button
                  className={`${styles.metricButton} ${activeMetric === 'quantity' ? styles.active : ''}`}
                  onClick={() => setActiveMetric('quantity')}
                >
                  {t('report.quantity')}
                </button>
                <button
                  className={`${styles.metricButton} ${activeMetric === 'orderCount' ? styles.active : ''}`}
                  onClick={() => setActiveMetric('orderCount')}
                >
                  {t('report.orderCount')}
                </button>
              </div>
            </div>
            <button onClick={handleExportExcel} className={styles.exportButton}>
              <Download size={18} />
              {t('report.exportToExcel')}
            </button>
          </div>

          <div className={styles.chartContainer}>
            <div style={{ height: '400px', position: 'relative' }}>
              {renderChart()}
            </div>
          </div>
        </div>
      )}

      {/* Data Table */}
      {summaryData && !isLoadingSummary && (
        <div className={styles.tableSection}>
          <div className={styles.tableHeader}>
            <h2>{t('report.dataTable')}</h2>
            {!shouldShowChart && (
              <button onClick={handleExportExcel} className={styles.exportButton}>
                <Download size={18} />
                {t('report.exportToExcel')}
              </button>
            )}
          </div>

          <div className={styles.tableContainer}>
            {filters.groupByProduct && filters.groupByDate ? renderPivotedTable() : renderStandardTable()}
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoadingSummary && !summaryData && (
        <div className={styles.loadingState}>
          <Loader2 className={styles.spinnerLarge} size={48} />
          <h3>{t('report.loadingData')}</h3>
        </div>
      )}

      {/* Empty State */}
      {!summaryData && !isLoadingSummary && (
        <div className={styles.emptyState}>
          <TrendingUp size={64} />
          <h3>{t('report.courierPerformance.noDataYet')}</h3>
          <p>{t('report.courierPerformance.selectFilters')}</p>
        </div>
      )}
    </div>
  );
};

export default SalesReportPage;